import {
	Text,
	Button,
	Box,
	Card,
	Stack,
	Overlay,
	useMantineTheme,
	Image,
	Flex,
	Group
} from "@mantine/core"
import styles from "./ShipCard.module.css"
import { useNavigate } from "react-router-dom"
import { Brands, BRANDS_NAMES } from "@utils/constants.util"
import { AEMImage } from "@components/AEMImage"
import { useFiltersPromotionStore } from "@stores/filtersPromotionStore"
import { useIsMobile, useIsTablet } from "@utils/device.utils"
import { eventTrackingItems } from "@utils/eventTracking"
import { useRedirect } from "@hooks/useRedirect"
import { LinkType } from "@models/link.model"
import { AEMDescriptionType } from "@AEM/models/AEMPage.model"
import { AEMSvgData } from "@components/AEMSvg"

interface CardImage {
	description: string
	height: number
	path: string
	title: string
	width: number
}

export interface ShipCardProps {
	brand: { brandName: string; brandCode?: string; icon?: Partial<AEMSvgData> }
	image: CardImage
	title: string
	description: string
	longDescription: AEMDescriptionType
	primaryLinkText: string
	primaryLinkUrl: string
	pretitle?: string
	subtitle?: string
	primaryLinkType?: string
	cardIndex?: number
	classNames?: Record<string, string>
	exploringFleet?: boolean
}

export const ShipCard = ({ card }: { card: ShipCardProps }) => {
	const { setBrand, resetFilters } = useFiltersPromotionStore()
	const navigate = useNavigate()
	const { handleRedirect } = useRedirect()
	const isMobile = useIsMobile()
	const isTablet = useIsTablet()
	const theme = useMantineTheme()
	const cardColourScheme =
		card.brand?.brandName === BRANDS_NAMES.ROYAL
			? theme.colors.royal[2]
			: card.brand?.brandName === BRANDS_NAMES.CELEBRITY
				? theme.colors.royal[3]
				: theme.colors.royal[4]

	const { exploringFleet = false } = card

	const handleExploreClick = () => {
		resetFilters()
		setBrand(
			card.title === BRANDS_NAMES.ROYAL ? Brands.ROYAL : Brands.CELEBRITY
		)
		navigate({
			pathname: "/promotion/brand"
		})
		promotionIinteraction(card)
	}

	const shiplocatorClick = (link?: string) => {
		eventTrackingItems({
			event_name: "ship_locator_search",
			link_url: link ? link : "",
			search_term: "",
			searchbarName: "Ship Locator",
			event222: 1
		})
		handleRedirect({
			link: link,
			type: (card.primaryLinkType as LinkType) ?? "public"
		})
	}

	const promotionIinteraction = (card?: ShipCardProps) => {
		eventTrackingItems({
			event_name: "promotion_click",
			category: "Promo Click",
			clickText: card?.primaryLinkText,
			link_url: "/promotion/brand",
			event211: 1
		})
	}

	const handleUserClick = () => {
		if (exploringFleet) {
			shiplocatorClick(card.primaryLinkUrl)
		} else {
			handleExploreClick()
		}
	}

	const textBoxHeight = exploringFleet
		? isMobile
			? 100
			: 116
		: isMobile
			? 127
			: 180

	return (
		<>
			<Stack gap={0} maw={isMobile || isTablet ? 328 : 387}>
				<Card
					mb={isMobile || isTablet ? 22 : 0}
					p={0}
					pb={18}
					shadow="0px 4px 22px 0px var(--shadow-dark-card)"
					bg="var(--background-color-card)"
				>
					<Box pos="relative">
						<AEMImage
							imageData={{
								...card.image,
								height: isMobile || isTablet ? 258 : 435,
								width: isMobile || isTablet ? 328 : 435
							}}
						/>
						<Box
							pos="absolute"
							top="50%"
							left="50%"
							className={styles.logoContainer}
						>
							<Flex align="center" justify="center" p={20}>
								<Image
									src={card?.brand?.icon?.path}
									alt={`${card?.brand?.brandName} logo`}
									width="100%"
									height="100%"
									fit="contain"
									style={{ filter: "brightness(0) invert(1)" }}
								/>
							</Flex>
						</Box>
					</Box>
					<Stack gap={0}>
						<Stack
							px={isMobile || isTablet ? 8 : "lg"}
							justify="center"
							align="center"
							mih={textBoxHeight}
							className={styles.cardInfo}
						>
							<Overlay zIndex={50} h={8} bg={cardColourScheme} />
							<Box>
								<Text
									fz={{ base: 14, sm: 16 }}
									fw={{ base: 600, sm: 700 }}
									ta="center"
									mb={4}
								>
									{card.title}{" "}
								</Text>

								<Text
									fz={{ base: 14, sm: 16 }}
									fw={400}
									ta="center"
									w="100%"
									lh={1.5}
								>
									{card.longDescription?.markdown}
								</Text>
							</Box>
						</Stack>
					</Stack>
				</Card>
				<Group
					className={styles.buttonWrapper}
					align="flex-end"
					justify="center"
					bottom={0}
					w="100%"
				>
					<Button
						size="md"
						onClick={handleUserClick}
						bg={cardColourScheme}
						h={"fit-content"}
						data-qa={`promos.home.card.button.${card?.brand?.brandCode}`}
						maw={isMobile ? 200 : 250}
						py={isMobile ? 6 : 8}
					>
						<Text
							fz={isMobile ? 16 : 18}
							lh={1.2}
							fw={600}
							style={{
								whiteSpace: "normal"
							}}
						>
							{card.primaryLinkText}
						</Text>
					</Button>
				</Group>
			</Stack>
		</>
	)
}
